import { InputSelect, InputWrapper } from '@vrplatform/components';
import { useInfiniteQuery, useQuery, useTeam } from '@vrplatform/data';
import { PlusIcon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import { toTitleCase } from '@vrplatform/utils';
import { Center, Text } from '@mantine/core';
import {
  type account_bool_exp,
  defaultAccountOrderBy,
} from '@vrplatform/graphql';
import { formatAssignmentType, whereAccounts } from '@vrplatform/ui-common';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useFeeForm } from '../useFeeForm';

type Props = {
  type: 'credit' | 'debit';
};

export const FeeAccountEditor = ({ type }: Props) => {
  const methods = useFeeForm();

  return (
    <Controller
      control={methods.control}
      name={type === 'credit' ? 'creditAccountId' : 'debitAccountId'}
      render={({ field, fieldState }) => {
        return (
          <InputWrapper error={fieldState.error?.message} w="100%">
            <AccountSelect
              accountId={field.value}
              setAccountId={field.onChange}
              error={!!fieldState.error}
              disabled={field.disabled}
            />
          </InputWrapper>
        );
      }}
    />
  );
};

const AccountSelect = ({
  accountId,
  setAccountId,
  error,
  disabled,
}: {
  accountId: string | undefined;
  setAccountId: (accountId: 'create_new' | ({} & string) | undefined) => void;
  error: boolean;
  disabled: boolean | undefined;
}) => {
  const [search, setSearch] = useState('');
  const [{ id: teamId }] = useTeam();

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: account_bool_exp = {
        ...whereAccounts({
          search: args.search,
          tenantId: args.teamId,
        }),
        type: {
          _neq: 'nonPosting',
        },
      };

      const list = q
        .accounts({
          where,
          limit,
          offset,
          order_by: defaultAccountOrderBy,
        })
        .map<SelectItem>((account) => {
          const assignments = account
            .assignments({
              where: {
                type: { _in: ['accountsPayable', 'accountsReceivable'] },
              },
            })
            .map((a) => formatAssignmentType(a.type));

          const disabled = !!assignments.length;

          const disabledDescription =
            assignments.length === 1
              ? `This account is assigned to ${assignments[0]}`
              : `This account is assigned to ${assignments.join(' & ')}`;

          return {
            value: account.id,
            label: account.title || 'No name',
            disabled,
            group: toTitleCase(account.category?.classification || '-'),

            description: disabled ? disabledDescription : undefined,
          };
        });

      const aggregate = q.accountAggregate({ where }).aggregate?.count() || 0;

      return {
        aggregate,
        list,
      };
    },
    {
      variables: {
        search: search.trim(),
        teamId,
      },
    }
  );

  const {
    data,
    isLoading: loadingValue,
    error: queryError,
  } = useQuery(
    (q, args) => {
      if (!args.accountId)
        return {
          value: null,
        };

      if (args.accountId === 'create_new')
        return {
          value: {
            label: (<Text c="neutral">Create new account on save</Text>) as any,
            value: 'create_new',
            icon: (
              <Center>
                <PlusIcon size={16} />
              </Center>
            ),
          },
        };

      return {
        value:
          q
            .accounts({ where: { id: { _eq: args.accountId } } })
            .map<SelectItem>((account) => ({
              value: account.id,
              label: account.title!,
            }))[0] || null,
      };
    },
    {
      variables: {
        accountId,
      },
    }
  );

  return (
    <InputSelect
      type="single"
      setValue={(v) => v?.value && setAccountId(v?.value)}
      value={data?.value || null}
      infiniteData={{ ...queryData, setSearch }}
      inputProps={{
        loadingQuery: loadingValue,
        error: error || !!queryError,
        placeholder: 'Select account',
        disabled,
      }}
      dropdownProps={{
        width: 'target',
      }}
      pinnedItems={[
        {
          label: 'Create new account',
          value: 'create_new',
          icon: (
            <Center>
              <PlusIcon size={16} />
            </Center>
          ),
        },
      ]}
    />
  );
};
